import React from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { KeyRound } from "lucide-react";

/** Global modal that blocks the app until a user who signed in with a super-admin
 * issued temporary password sets their own. The flag `must_change_password` is
 * returned on login and cleared automatically when the change succeeds. */
export default function ForcePasswordChange() {
    const { user, setUser } = useAuth();
    const [current, setCurrent] = React.useState("");
    const [next, setNext] = React.useState("");
    const [confirm, setConfirm] = React.useState("");
    const [busy, setBusy] = React.useState(false);

    if (!user || !user.must_change_password) return null;

    async function submit(e) {
        e.preventDefault();
        if (next.length < 8) return toast.error("Password must be at least 8 characters");
        if (next !== confirm) return toast.error("Passwords don't match");
        if (busy) return;
        setBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: current, new_password: next });
            toast.success("Password updated — welcome back");
            setUser({ ...user, must_change_password: false });
            setCurrent(""); setNext(""); setConfirm("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't change password");
        } finally { setBusy(false); }
    }

    return (
        <Dialog open modal>
            <DialogContent className="sm:max-w-md" data-testid="force-password-change" onPointerDownOutside={e => e.preventDefault()} onEscapeKeyDown={e => e.preventDefault()}>
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                        <KeyRound className="w-5 h-5 text-primary" /> Set a new password
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <p className="text-sm text-brand-stone">
                        Your temporary password was issued by an admin. Please choose your own password before continuing.
                    </p>
                    <div>
                        <Label>Temporary password</Label>
                        <Input type="password" value={current} onChange={e => setCurrent(e.target.value)} required autoFocus data-testid="force-current" className="mt-1.5" />
                    </div>
                    <div>
                        <Label>New password</Label>
                        <Input type="password" value={next} onChange={e => setNext(e.target.value)} required minLength={8} data-testid="force-new" className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Confirm new password</Label>
                        <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8} data-testid="force-confirm" className="mt-1.5" />
                    </div>
                    <Button type="submit" disabled={busy} data-testid="force-submit" className="w-full bg-primary hover:bg-primary/90">
                        {busy ? "Saving…" : "Set password"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
